import { t } from '@glory/i18n';
import { CircleAlert } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { createContext, type ReactNode, type Ref, useContext, useId, useMemo } from 'react';

import { type InfoHintContent, InfoHint } from '../components/info-hint.tsx';
import { Spinner } from '../components/spinner.tsx';
import { cn } from '../lib/cn.ts';
import { collapse } from '../lib/motion.tsx';

export type FieldSize = 'sm' | 'md' | 'lg';

interface FieldState {
  readonly id: string;
  readonly labelId: string;
  readonly descriptionId: string;
  readonly errorId: string;
  readonly hasDescription: boolean;
  readonly invalid: boolean;
  readonly required: boolean;
  readonly disabled: boolean;
  readonly readOnly: boolean;
  readonly loading: boolean;
}

const FieldContext = createContext<FieldState | null>(null);

/** Состояние обёртки для элемента ввода; вне `Field` — пустое (поле в ячейке таблицы, в поиске). */
export function useFieldState(): FieldState | null {
  return useContext(FieldContext);
}

/** Атрибуты элемента ввода: связь с подписью, подсказкой и ошибкой для экранных дикторов. */
export function useFieldControl(overrides: { id?: string | undefined } = {}) {
  const field = useFieldState();
  if (!field) return { id: overrides.id };
  const describedBy = [
    field.hasDescription ? field.descriptionId : null,
    field.invalid ? field.errorId : null,
  ]
    .filter(Boolean)
    .join(' ');
  return {
    id: overrides.id ?? field.id,
    'aria-describedby': describedBy === '' ? undefined : describedBy,
    'aria-invalid': field.invalid || undefined,
    'aria-required': field.required || undefined,
    disabled: field.disabled || undefined,
    readOnly: field.readOnly || undefined,
  };
}

/**
 * Атрибуты группы (сегменты, карточки-варианты): `<label for>` на группу не действует,
 * поэтому подпись связывается через `aria-labelledby`.
 */
export function useFieldGroupControl() {
  const field = useFieldState();
  const { id: _id, disabled, readOnly: _readOnly, ...control } = useFieldControl();
  return {
    ...control,
    'aria-labelledby': field?.labelId,
    disabled,
  };
}

export interface FieldProps {
  readonly label: ReactNode;
  /** Подпись только для экранных дикторов — поле в ячейке таблицы или в строке фильтров. */
  readonly hideLabel?: boolean;
  /** Значок «i» с объяснением у подписи: как работает поле или расчёт. */
  readonly hint?: InfoHintContent;
  /** Подсказка под полем (формат, пример); ошибка её заменяет. */
  readonly description?: ReactNode;
  /** Ошибка на русском: поле подсвечивается, текст — под полем. */
  readonly error?: string | null | undefined;
  readonly required?: boolean;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  /** Значение загружается или проверяется на сервере. */
  readonly loading?: boolean;
  /** Справа под полем: счётчик символов и подобное. */
  readonly aside?: ReactNode;
  readonly id?: string;
  readonly className?: string;
  readonly children: ReactNode;
}

/**
 * Обёртка любого поля: подпись, обязательность, «i», подсказка, ошибка, состояния.
 * Элемент ввода внутри берёт атрибуты через `useFieldControl`.
 */
export function Field({
  label,
  hideLabel = false,
  hint,
  description,
  error,
  required = false,
  disabled = false,
  readOnly = false,
  loading = false,
  aside,
  id,
  className,
  children,
}: FieldProps) {
  const generated = useId();
  const baseId = id ?? `field-${generated}`;
  const invalid = typeof error === 'string' && error !== '';
  const hasDescription = description !== undefined && description !== null;
  const state = useMemo<FieldState>(
    () => ({
      id: baseId,
      labelId: `${baseId}-label`,
      descriptionId: `${baseId}-description`,
      errorId: `${baseId}-error`,
      hasDescription: hasDescription && !invalid,
      invalid,
      required,
      disabled,
      readOnly,
      loading,
    }),
    [baseId, hasDescription, invalid, required, disabled, readOnly, loading],
  );

  return (
    <FieldContext value={state}>
      <div className={cn('flex min-w-0 flex-col gap-1', className)} data-invalid={invalid || undefined}>
        <div className={cn('flex min-h-4 items-center gap-1', hideLabel && 'sr-only')}>
          <label
            id={state.labelId}
            htmlFor={baseId}
            className={cn('text-small font-bold text-text', disabled && 'text-muted')}
          >
            {label}
          </label>
          {required ? (
            <span className="size-1 shrink-0 rounded-full bg-text" title={t('fields.required')}>
              <span className="sr-only">{t('fields.required')}</span>
            </span>
          ) : null}
          {hint ? <InfoHint hint={hint} /> : null}
        </div>
        {children}
        {/* Строка под полем раскрывается и схлопывается: появившаяся ошибка не сдвигает всё ниже за один
            кадр. Отступ в 4 px (gap колонки) компенсирован `-mt-1` и вернулся внутрь как `pt-1`: у схлопнутой
            строки не остаётся лишнего зазора. */}
        <AnimatePresence initial={false}>
          {invalid || hasDescription || aside !== undefined ? (
            <motion.div key="note" {...collapse} className="-mt-1">
              <div className="flex items-start gap-2 pt-1">
                {invalid ? (
                  <p
                    id={state.errorId}
                    className="flex flex-1 items-start gap-1 text-caption font-semibold text-danger"
                  >
                    <CircleAlert className="mt-px size-3.5 shrink-0" aria-hidden="true" />
                    <span>{error}</span>
                  </p>
                ) : hasDescription ? (
                  <p id={state.descriptionId} className="flex-1 text-caption text-muted">
                    {description}
                  </p>
                ) : (
                  <span className="flex-1" />
                )}
                {aside === undefined ? null : (
                  <div className="shrink-0 text-caption text-muted tabular-nums">{aside}</div>
                )}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </FieldContext>
  );
}

const BOX_SIZE: Record<FieldSize, string> = {
  sm: 'min-h-7 px-2 text-small',
  md: 'min-h-8 px-2.5 text-body',
  lg: 'min-h-10 px-3 text-lead',
};

export interface FieldBoxProps {
  readonly size?: FieldSize;
  /** `cell` — редактор ячейки таблицы: без рамки, на всю ячейку. */
  readonly variant?: 'default' | 'cell';
  /** Слева от ввода: иконка, флаг страны, префикс. */
  readonly leading?: ReactNode;
  /**
   * Справа от ввода: единицы («₽», «ч»), действия (показать пароль, скопировать, очистить).
   * Сюда же встанет кнопка голосового ввода после интеграции ИИ.
   */
  readonly trailing?: ReactNode;
  /** Многострочный ввод: слоты прижаты к верху. */
  readonly multiline?: boolean;
  /** Состояния без `Field` вокруг (поиск, ячейка таблицы); внутри `Field` берутся из него. */
  readonly disabled?: boolean | undefined;
  readonly readOnly?: boolean | undefined;
  readonly invalid?: boolean | undefined;
  readonly className?: string | undefined;
  readonly children: ReactNode;
  /** Для всплывающих панелей, привязанных к рамке (календарь, список времени). */
  readonly ref?: Ref<HTMLDivElement>;
}

/** Классы рамки поля: у `FieldBox` и у кнопок, которые выглядят как поле (выбор из списка, дата). */
export function fieldBoxClass({
  size = 'md',
  variant = 'default',
  multiline = false,
  invalid = false,
  disabled = false,
  readOnly = false,
  className,
}: {
  size?: FieldSize | undefined;
  variant?: 'default' | 'cell' | undefined;
  multiline?: boolean | undefined;
  invalid?: boolean | undefined;
  disabled?: boolean | undefined;
  readOnly?: boolean | undefined;
  className?: string | undefined;
}) {
  return cn(
    'group/field flex w-full min-w-0 gap-1.5 text-text transition-colors',
    multiline ? 'items-start py-1.5' : 'items-center',
    variant === 'default' && [
      'rounded-md border-thick border-border bg-surface',
      'hover:border-muted focus-within:border-focus hover:focus-within:border-focus',
      invalid &&
        'border-danger hover:border-danger focus-within:border-danger hover:focus-within:border-danger',
      readOnly && 'border-dashed border-border bg-transparent hover:border-border',
      disabled && 'cursor-not-allowed border-border bg-surface-2 opacity-60 hover:border-border',
    ],
    variant === 'cell' && 'h-full bg-transparent',
    BOX_SIZE[size],
    className,
  );
}

/** Рамка поля ввода со слотами; состояние (ошибка, отключено, загрузка) — из `Field`. */
export function FieldBox({
  size = 'md',
  variant = 'default',
  leading,
  trailing,
  multiline = false,
  className,
  children,
  ref,
  ...state
}: FieldBoxProps) {
  const field = useFieldState();
  const invalid = state.invalid ?? field?.invalid ?? false;
  const disabled = state.disabled ?? field?.disabled ?? false;
  const readOnly = state.readOnly ?? field?.readOnly ?? false;
  const loading = field?.loading ?? false;
  return (
    <div
      ref={ref}
      className={fieldBoxClass({ size, variant, multiline, invalid, disabled, readOnly, className })}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
    >
      {leading === undefined ? null : (
        <span className="flex shrink-0 items-center text-muted [&_svg]:size-4">{leading}</span>
      )}
      {children}
      {loading ? <Spinner className="shrink-0 text-muted" /> : null}
      {trailing === undefined ? null : (
        <span className="flex shrink-0 items-center gap-0.5 text-muted [&_svg]:size-4">{trailing}</span>
      )}
    </div>
  );
}

/** Общие классы элемента ввода внутри `FieldBox`. */
export const inputClass = cn(
  'min-w-0 flex-1 self-stretch bg-transparent text-inherit outline-none',
  'placeholder:text-muted disabled:cursor-not-allowed',
);

const actionClass = cn(
  'inline-flex size-6 items-center justify-center rounded-sm text-muted transition-colors',
  'hover:bg-surface-2 hover:text-text focus-visible:bg-surface-2 focus-visible:text-text focus-visible:outline-none',
  'disabled:pointer-events-none',
);

/** Кнопка-действие в слоте поля: показать пароль, скопировать, очистить. */
export function FieldAction({
  label,
  onClick,
  children,
  pressed,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  pressed?: boolean;
}) {
  const field = useFieldState();
  return (
    <button
      type="button"
      className={actionClass}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={field?.disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Действие-ссылка в слоте поля: позвонить, написать, открыть сайт. */
export function FieldActionLink({
  label,
  href,
  external = false,
  children,
}: {
  label: string;
  href: string;
  /** Открыть в новой вкладке (мессенджер, сайт). */
  external?: boolean;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      className={actionClass}
      aria-label={label}
      title={label}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

/** Свойства обёртки у полей-«комбайнов» (подпись, ошибка, состояния) наравне со свойствами ввода. */
export type FieldPart = Omit<FieldProps, 'children'>;

/** Отделить свойства обёртки от свойств элемента ввода; `id` и состояния остаются у `Field`. */
export function splitField<P extends FieldPart>(props: P): [FieldPart, Omit<P, keyof FieldPart>] {
  const {
    label,
    hideLabel,
    hint,
    description,
    error,
    required,
    disabled,
    readOnly,
    loading,
    aside,
    id,
    className,
    ...rest
  } = props;
  return [
    {
      label,
      hideLabel,
      hint,
      description,
      error,
      required,
      disabled,
      readOnly,
      loading,
      aside,
      id,
      className,
    },
    rest,
  ];
}
